import { chartDebugLog } from './debug/browser-logging'

type NotificationOptions = {
  displayTime?: number
}

type DxChartNotificationApi = {
  sendNotification: (msg: string, options?: NotificationOptions) => void
}

type QueuedNotification = {
  message: string
  options: NotificationOptions
}

const NOTIFICATION_DURATIONS = {
  success: 2000,
  error: 5000,
  connectionLost: 3600000,
} as const

const CONNECTION_LOST_MESSAGE = 'Connection lost. Attempting to reconnect...'
const CONNECTION_RESTORED_MESSAGE = 'Reconnected'

let chartApiRef: DxChartNotificationApi | null = null
const pendingQueue: QueuedNotification[] = []
let connectionLostActive = false

const sendWithDxChart = (message: string, options: NotificationOptions): void => {
  if (!chartApiRef) {
    pendingQueue.push({ message, options })
    return
  }

  chartApiRef.sendNotification(message, options)
}

const flushPendingQueue = (): void => {
  if (!chartApiRef || pendingQueue.length === 0) {
    return
  }

  while (pendingQueue.length > 0) {
    const next = pendingQueue.shift()
    if (!next) {
      continue
    }
    chartApiRef.sendNotification(next.message, next.options)
  }
}

export const setDxChartNotificationApi = (api: DxChartNotificationApi | null): void => {
  chartApiRef = api

  if (!api) {
    // Drop pending notifications on teardown to avoid stale messages on next mount.
    pendingQueue.length = 0
    connectionLostActive = false
    return
  }

  flushPendingQueue()
}

export const notifyError = (title: string, detail?: string) => {
  const message = detail ? `${title}: ${detail}` : title
  console.error('[Notification] Error:', message)
  sendWithDxChart(message, { displayTime: NOTIFICATION_DURATIONS.error })
}

export const notifySuccess = (title: string) => {
  chartDebugLog('notification', 'success shown', { title })
  sendWithDxChart(title, { displayTime: NOTIFICATION_DURATIONS.success })
}

export const notifyConnectionLost = () => {
  if (connectionLostActive) {
    return
  }

  connectionLostActive = true
  chartDebugLog('notification', 'connection lost shown', {})
  sendWithDxChart(CONNECTION_LOST_MESSAGE, { displayTime: NOTIFICATION_DURATIONS.connectionLost })
}

export const notifyConnectionRestored = () => {
  connectionLostActive = false
  chartDebugLog('notification', 'connection restored shown', {})
  sendWithDxChart(CONNECTION_RESTORED_MESSAGE, { displayTime: NOTIFICATION_DURATIONS.success })
}
